import { isAxiosError } from "axios";
import axios from "../config/axios";
import { currentTenantScope, type ValidatedTenantScope } from "../contexts/tenantScopeHeaders";
import { clearOnSignOut } from "../utils/signOutCleanup";
import { PolicyRaisedError } from "./responseAdmission";
import {
    acceptResourceAccess,
    accessLeaseExpired,
    accessTicketIsCurrent,
    captureAccessTicket,
    offlineAccessAllowed,
    setAdmissionUnavailable,
    rejectResourceAccess,
    resourceAccessState,
    resumeOfflineResourceAccess,
    subscribeResourceAccess,
    synchronizeNativeResourceAccess,
    type AccessTicket,
    type ResourceAccess,
} from "./resourceAccess";

type RefreshState = { ticket: AccessTicket; fetching: boolean; error: unknown };
let state: RefreshState = { ticket: captureAccessTicket(), fetching: false, error: null };
let pending:
    | { ticket: AccessTicket; controller: AbortController; promise: Promise<ResourceAccess | null> }
    | undefined;
subscribeResourceAccess(() => {
    if (pending && !accessTicketIsCurrent(pending.ticket)) pending.controller.abort();
});
const listeners = new Set<() => void>();
export const subscribeAdmissionRefresh = (listener: () => void) => {
    listeners.add(listener);
    return () => {
        listeners.delete(listener);
    };
};
/** How a failed admission left the server: `transport` for no response,
 * `fault` for a 5xx or 429 that carried no access decision (the service is
 * unwell; honoured for a bounded grace only), `decision` for any other answer,
 * including a 5xx that carries `X-Access-Admission`, which is the server's own
 * verdict and not an outage. Mirrors `NativeResourceAccess` on iOS. */
function admissionFailureKind(error: unknown): "transport" | "fault" | "decision" {
    if (!isAxiosError(error)) return "decision";
    const response = error.response;
    if (!response) return "transport";
    const headers = response.headers as
        { get?: (name: string) => unknown } | Record<string, unknown> | undefined;
    const decided =
        typeof headers?.get === "function"
            ? headers.get("x-access-admission") != null
            : headers != null &&
              Object.keys(headers).some((name) => name.toLowerCase() === "x-access-admission");
    if (decided) return "decision";
    return response.status >= 500 || response.status === 429 ? "fault" : "decision";
}
export const admissionRefreshState = () => state;

/** An offered answer older than this is not used: its lease would be spent or nearly so. */
const ADMITTED_ACCESS_MAX_AGE_MS = 30_000;
/** The `/project-access/me` body company-session admitted a scope with
 *  (`CompanySessionResponse.project_access`), offered by the admission owner for that scope's
 *  first admission only. */
let admittedAccess: {
    scope: ValidatedTenantScope;
    value: ResourceAccess;
    receivedAt: number;
} | null = null;
clearOnSignOut(() => {
    admittedAccess = null;
});

/** Offers the first admission of `scope`, the binding just installed, the body company-session
 *  answered with, so it is not asked for again. */
export function offerAdmittedAccess(
    scope: ValidatedTenantScope,
    value: ResourceAccess,
    receivedAt: number,
): void {
    admittedAccess = { scope, value, receivedAt };
}

/** The offer, once: only for the binding it was offered with, while this scope holds no lease
 *  yet, and while it is fresh. */
function takeAdmittedAccess() {
    const offer = admittedAccess;
    admittedAccess = null;
    return offer &&
        offer.scope === currentTenantScope() &&
        !resourceAccessState() &&
        Date.now() - offer.receivedAt < ADMITTED_ACCESS_MAX_AGE_MS
        ? offer
        : null;
}
function publish(ticket: AccessTicket, fetching: boolean, error: unknown) {
    state = { ticket, fetching, error };
    listeners.forEach((listener) => listener());
}

/** All consumers share the lease, the in-flight HTTP request and native fence.
 * Mounting a panel never renews a fresh lease. Explicit Retry and the single
 * provider-owned renewal timer may request a fresh server decision.
 */
export function refreshResourceAdmission(force = false): Promise<ResourceAccess | null> {
    const existing = resourceAccessState();
    // A background renewal must not turn an already verified lease into a
    // first-paint network dependency, including when connectivity drops while
    // that renewal is in flight. This reuses authority without extending it.
    if (!force && existing?.admitted && existing.active && !accessLeaseExpired()) {
        const ticket = captureAccessTicket();
        return synchronizeNativeResourceAccess().then(
            () =>
                accessTicketIsCurrent(ticket) && !accessLeaseExpired()
                    ? resourceAccessState()
                    : null,
            () => {
                if (accessTicketIsCurrent(ticket)) rejectResourceAccess();
                return null;
            },
        );
    }
    if (pending && accessTicketIsCurrent(pending.ticket)) return pending.promise;
    const request = {
        ticket: captureAccessTicket(),
        controller: new AbortController(),
        promise: Promise.resolve<ResourceAccess | null>(null),
    };
    pending = request;
    request.promise = (async () => {
        let nativeReady = false;
        try {
            await synchronizeNativeResourceAccess();
            if (!accessTicketIsCurrent(request.ticket)) return null;
            nativeReady = true;
            // The answer company-session admitted this scope with stands in for the first /me:
            // the same identity check, and its lease counts from when it arrived.
            const admitted = takeAdmittedAccess();
            if (!admitted && !navigator.onLine) {
                resumeOfflineResourceAccess();
                if (!offlineAccessAllowed()) throw new Error("Connect to verify project access");
                return resourceAccessState();
            }
            const { value, receivedAt } = admitted ?? {
                value: (
                    await axios.get<ResourceAccess>("/api/ai/project-access/me", {
                        timeout: 30_000,
                        signal: request.controller.signal,
                    })
                ).data,
                receivedAt: undefined,
            };
            if (!accessTicketIsCurrent(request.ticket)) return null;
            const selected = currentTenantScope();
            if (
                selected &&
                (value.project !==
                    (selected.accessProject ??
                        (selected.urlRouteId ? null : `project:${selected.projectId}`)) ||
                    value.organization_id !== selected.organizationId ||
                    value.member_id !== selected.principalId)
            ) {
                rejectResourceAccess();
                throw new Error("Project access identity does not match the selected session");
            }
            acceptResourceAccess(value, request.ticket, receivedAt);
            request.ticket = captureAccessTicket();
            nativeReady = false;
            await synchronizeNativeResourceAccess();
            if (!accessTicketIsCurrent(request.ticket)) return null;
            nativeReady = true;
            if (!value.active) throw new Error("Project access is inactive");
            return resourceAccessState();
        } catch (error) {
            if (!accessTicketIsCurrent(request.ticket)) return null;
            if (!nativeReady) rejectResourceAccess();
            else {
                const kind = admissionFailureKind(error);
                setAdmissionUnavailable(kind === "decision" ? false : kind);
                // Online expiry (on wake, say) suspended the lease; an outage
                // leaves its original offline window standing.
                if (kind !== "decision") resumeOfflineResourceAccess();
            }
            request.ticket = captureAccessTicket();
            publish(request.ticket, false, error);
            // An outage does not extend the original bounded lease. The mounted
            // provider may keep it only while its independent expiry check passes.
            return null;
        } finally {
            if (pending === request) {
                pending = undefined;
                if (accessTicketIsCurrent(request.ticket) && state.fetching)
                    publish(request.ticket, false, null);
            }
        }
    })();
    publish(request.ticket, true, null);
    return request.promise;
}

/**
 * Sends a command the server keys by its request id, so sending it again replays its recorded
 * outcome instead of acting twice. A command whose own write raised the project's policy has its
 * answer fenced and its page retired; once the same account is admitted again to the same project
 * it is sent once more, and the server checks the caller under the new policy before answering
 * what the first send did. A refresh another sign-in or project superseded answers null, so the
 * command never reaches the account or project that replaced it: `send` goes out as the account
 * signed in when it is called (`DocumentWorkflowsApi.change` binds itself with `sentAsSignedIn`), and
 * the replay is called only while that is still the one it began under. Any other failure, or
 * access that does not come back, rejects as before.
 */
export async function replayAfterPolicyRaise<T>(send: () => Promise<T>): Promise<T> {
    const account = captureAccessTicket().scope;
    const project = resourceAccessState()?.project;
    try {
        return await send();
    } catch (error) {
        if (!(error instanceof PolicyRaisedError)) throw error;
        // A refresh another sign-in or project superseded answers null, whatever then holds the lease.
        if (!(await refreshResourceAdmission(true))) throw error;
        const access = resourceAccessState();
        if (
            !access?.active ||
            !access.admitted ||
            accessLeaseExpired() ||
            access.project !== project ||
            captureAccessTicket().scope !== account
        )
            throw error;
        return send();
    }
}
